import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import tiLogoMark from '../assets/TI-logo-no-text.png';
import ThemeToggle from './ThemeToggle';
import { ALLOWED_DOMAIN } from '../config';
import './Login.css';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export default function Login() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [gsiReady, setGsiReady] = useState(false);
  const { signInWithGoogle } = useAuth();
  // Google's official button is rendered here, invisibly overlaid on
  // top of our styled custom button. Click events pass through to
  // Google's button, which handles FedCM reliably. We get our visual
  // styling and Google's working auth flow.
  const overlayRef = useRef(null);

  const handleCredentialResponse = useCallback(async (response) => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle(response);
    } catch (err) {
      setError(err.message || 'Sign-in failed. Please try again.');
      console.error('Auth error:', err);
    }
    setLoading(false);
  }, [signInWithGoogle]);

  useEffect(() => {
    function initializeGSI() {
      if (!window.google || !overlayRef.current) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        hosted_domain: ALLOWED_DOMAIN,
      });

      // Render Google's button at a fixed pixel size; it gets covered
      // by our styled button via CSS (opacity 0, but interactive).
      // 400 px is wide enough that any reasonable button width is
      // fully covered by the underlying Google click target.
      overlayRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(overlayRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 400,
      });

      setGsiReady(true);
    }

    if (window.google) {
      initializeGSI();
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (window.google) {
        clearInterval(checkInterval);
        initializeGSI();
        return;
      }
      if (Date.now() - startTime > 10000) {
        clearInterval(checkInterval);
        setError('Google Sign-In could not be loaded. Please refresh the page or disable any content blockers.');
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [handleCredentialResponse]);

  return (
    <div className="login-page">
      <ThemeToggle className="theme-toggle-corner" />
      <div className="login-card">
        <div className="login-logo">
          <img src={tiLogoMark} alt="Terra Inspectioneering" className="login-logo-img" />
        </div>
        <h1 className="login-title">Terra Inspectioneering Base</h1>

        <div className="login-google-btn-wrap">
          {/* Visible styled button — purely decorative; the real click
              target is the invisible Google button overlaid on top. */}
          <div
            className="login-google-btn"
            aria-hidden="true"
            style={{ opacity: gsiReady ? 1 : 0.6 }}
          >
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </div>
          {/* Google's real button, sized to fully cover the styled one. */}
          <div className="login-google-btn-overlay" ref={overlayRef} />
        </div>

        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        <p className="login-hint">
          Use your <strong>@{ALLOWED_DOMAIN}</strong> account to sign in.
        </p>
      </div>
    </div>
  );
}
